import { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Carousel,
  Col,
  Container,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AiFillIdcard } from "react-icons/ai";
import {
  FaArrowLeft,
  FaBox,
  FaCalendar,
  FaCalendarAlt,
  FaCarSide,
  FaCheckCircle,
  FaClock,
  FaEuroSign,
  FaExpand,
  FaImages,
  FaLocationArrow,
  FaMapMarkerAlt,
  FaQuestionCircle,
  FaRulerCombined,
  FaStar,
  FaTimes,
  FaTruck,
  FaWeight,
  FaWeightHanging,
  FaMoneyBill,
} from "react-icons/fa";
import { HiWrenchScrewdriver } from "react-icons/hi2";
import { formatNumber } from "../../utils/helpers";
import { userAPI } from "../../api/apiSystem";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import ClientChatBox from "../chat/ClientChatBox";
import "../clientTable/TransportClientDetails.css";
import CustomerRatingDisplay from "../rating/CustomerRatingDisplay";
import EmployeeOrderDetailsModal from "./EmployeeOrderDetailsModal";

export default function TransportEmployeeDetails() {
  const { t } = useTranslation();
  const { id, employeeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [transport, setTransport] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOrder, setShowOrder] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [ordersUnreadCounts, setOrdersUnreadCounts] = useState({});

  const [userType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // جلب بيانات الطلبات
  const fetchOrders = async () => {
    try {
      setError(null);

      const transportId = id;

      console.log("🔄 Fetching orders for transport ID:", transportId);

      const response = await userAPI.employees.byTransport(employeeId, transportId);

      console.log("📦 API Response:", response);

      if (response.success) {
        const ordersData = response.data.data || response.data;
        setOrders(ordersData);

        // جلب عدد الرسائل غير المقروءة لكل طلب
        await fetchUnreadCountsForOrders(ordersData);
      } else {
        setError(
          response.message || t("clientTransportDetails.error_occurred"),
        );
      }
    } catch (err) {
      console.error("❌ Error fetching orders:", err);
      setError(err.message || t("clientTransportDetails.error_occurred"));
    }
  };

  // دالة لجلب عدد الرسائل غير المقروءة لكل طلب
  const fetchUnreadCountsForOrders = async (ordersData) => {
    const unreadCounts = {};

    for (const order of ordersData) {
      try {
        const transportId = order.transport?.id || order.transport_id;
        const orderId = order.id;

        if (!transportId || !orderId) continue;

        console.log("📊 Fetching unread count for order:", orderId);

        const response = await userAPI.employees.getMessages(
          employeeId,
          transportId,
          orderId,
        );

        if (response.success) {
          let statistics = { unread_count: 0 };

          if (response.data && response.data.statistics) {
            statistics = response.data.statistics;
          } else if (
            response.data &&
            response.data.data &&
            response.data.data.statistics
          ) {
            statistics = response.data.data.statistics;
          }

          unreadCounts[orderId] = statistics.unread_count || 0;
          console.log(
            `📊 Unread count for order ${orderId}:`,
            statistics.unread_count,
          );
        }
      } catch (err) {
        console.error(
          `❌ Error fetching unread count for order ${order.id}:`,
          err,
        );
        unreadCounts[order.id] = 0;
      }
    }

    setOrdersUnreadCounts(unreadCounts);
  };

  // دالة لتحديث عدد الرسائل غير المقروءة لطلب معين
  const updateOrderUnreadCount = (orderId, newCount) => {
    setOrdersUnreadCounts((prev) => ({
      ...prev,
      [orderId]: newCount,
    }));
  };

  // ✅ Back
  const handleBack = () => {
    navigate(`/user/employees/${employeeId}/booking`, {
      state: {
        fromPage: location.state?.fromPage || 1,
        activeTab: location.state?.activeTab || "furniture",
      },
    });
  };

  const handleViewTrack = (booking_id) => {
    navigate(
      `/user/employees/${employeeId}/transport/transport_details/track//${booking_id}`,
    );
  };

  const handleViewLiveTracking = (id) => {
    navigate(
      `/user/employees/${employeeId}/transport/transport_details/live-track/${id}`,
    );
  };

  const translateStatus = (status) => {
    if (!status) return t("clientTransportDetails.status_unknown");
    const map = {
      completed: t("clientTransportDetails.status_completed"),
      pending: t("clientTransportDetails.status_pending"),
      accepted: t("clientTransportDetails.status_accepted"),
      rejected: t("clientTransportDetails.status_rejected"),
      assigned: t("clientTransportDetails.status_assigned"),
      received: t("clientTransportDetails.status_received"),
      approved: t("clientTransportDetails.status_approved"),
    };
    return map[status] || status;
  };

  const fetchTransportDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userAPI.employees.showTransport(employeeId, id);
      if (response.success) {
        setTransport(response.data.data || response.data);
      } else {
        setError(response.message || t("clientTransportDetails.error_occurred"));
      }
    } catch (err) {
      setError(err.message || t("clientTransportDetails.error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  // useEffect(() => {
  //   if (id && employeeId) fetchTransportDetails();
  //   else setLoading(false);
  // }, [id, employeeId]);

  useEffect(() => {
    if (id && employeeId) {
      fetchTransportDetails();
      fetchOrders();
    } else {
      setError(t("clientTransportDetails.no_transport_id"));
      setLoading(false);
    }
  }, [id, employeeId]);

  // ✅ view-only: فتح شات الطلب للقراءة فقط
  const handleShowOrder = (order) => {
    setSelectedOrder(order);
    setShowOrder(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("de-DE");
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case "completed": return "success";
      case "pending": return "warning";
      case "accepted": return "primary";
      case "rejected": return "danger";
      default: return "secondary";
    }
  };

  const PAYMENT_TERM_OPTIONS = [
    { value: "on_delivery", label: t("delivery_summary.paymentTerms.onDelivery") },
    { value: "7_days", label: t("delivery_summary.paymentTerms.after7Days") },
    { value: "14_days", label: t("delivery_summary.paymentTerms.after14Days") },
    { value: "30_days", label: t("delivery_summary.paymentTerms.after30Days") },
    { value: "3_days", label: t("delivery_summary.paymentTerms.after3Days") },
    { value: "by_agreement", label: t("delivery_summary.paymentTerms.byAgreement") },
  ];

  const ImageGallery = ({ images }) => {
    const [showModal, setShowModal] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    if (!images || images.length === 0) return null;

    return (
      <>
        <Card className="mb-4 shadow-sm border-0">
          <Card.Header className="bg-orange text-white fw-bold d-flex align-items-center">
            <FaImages className="me-2" />
            {t("driverCard.transportDetails.images")} ({images.length})
          </Card.Header>
          <Card.Body>
            <div className="d-flex flex-wrap gap-2">
              {images.slice(0, 4).map((image, index) => (
                <div
                  key={index}
                  className="position-relative"
                  style={{ cursor: "pointer" }}
                  onClick={() => { setSelectedIndex(index); setShowModal(true); }}
                >
                  <img
                    src={image}
                    alt={`Transport ${index + 1}`}
                    className="img-thumbnail"
                    style={{ width: 150, height: 150, objectFit: "cover", borderRadius: 8 }}
                  />
                  <div className="thumbnail-overlay"><FaExpand className="text-white" size={12} /></div>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Modal.Body className="text-center p-0">
            <Carousel activeIndex={selectedIndex} onSelect={setSelectedIndex} interval={null} indicators={images.length > 1} controls={images.length > 1}>
              {images.map((image, index) => (
                <Carousel.Item key={index}>
                  <img src={image} alt={`Transport ${index + 1}`} className="img-fluid" style={{ maxHeight: "70vh", objectFit: "contain" }} />
                </Carousel.Item>
              ))}
            </Carousel>
          </Modal.Body>
        </Modal>
      </>
    );
  };

  const newLocal = "#ff7b00";
  const orderStatus = transport?.order?.status?.toLowerCase();
  const showLiveTracking =
    Boolean(transport?.order) && !["pending", "completed"].includes(orderStatus);

  return (
    <>
      <NavbarMenu />
      <div className="transport-details-page">
        <Container className="py-4">
          {/* Back + View buttons (بدون حذف) */}
          <div className="d-flex flex-wrap gap-2 mb-4 align-items-center">
            <Button variant="secondary" onClick={handleBack} size="sm">
              <FaArrowLeft className="me-1" />
              {t("clientDetails.back")}
            </Button>

            <Button
              variant="outline"
              style={{ color: newLocal, borderColor: newLocal }}
              size="sm"
              onClick={() => handleViewTrack(transport?.booking_id)}
            >
              <FaTruck className="me-1" /> {t("clientDetails.track")}
            </Button>

            {showLiveTracking && (
              <Button variant="outline-success" size="sm" onClick={() => handleViewLiveTracking(transport.order?.id)}>
                <FaLocationArrow className="me-1" /> {t("clientDetails.live_tracking")}
              </Button>
            )}
          </div>

          {/* Progress */}
          <div className="progress-container">
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-center">
                <div className={`progress-step-circle ${["approved","assigned","received","completed"].includes(transport?.status) ? "completed" : ""}`}>
                  <FaCheckCircle size={24} className={`icon ${["approved","assigned","received","completed"].includes(transport?.status) ? "text-orange" : ""}`} />
                </div>
                <p className="progress-label">{t("clientTransportDetails.created")}</p>
              </div>
              <div className={`progress-line ${["received","completed"].includes(transport?.status) ? "active" : ""}`} />
              <div className="text-center">
                <div className={`progress-step-circle ${["received","completed"].includes(transport?.status) ? "active" : ""}`}>
                  <FaCarSide size={24} className={`icon ${["received","completed"].includes(transport?.status) ? "text-white" : ""}`} />
                </div>
                <p className="progress-label">{t("clientTransportDetails.on_the_way")}</p>
              </div>
              <div className={`progress-line ${transport?.status === "completed" ? "active" : ""}`} />
              <div className="text-center">
                <div className={`progress-step-circle ${transport?.status === "completed" ? "completed" : ""}`}>
                  <FaCheckCircle size={24} className={`icon ${transport?.status === "completed" ? "text-orange" : ""}`} />
                </div>
                <p className="progress-label">{t("clientTransportDetails.completed_status")}</p>
              </div>
            </div>
          </div>

          <h4 className="main-title">{t("clientTransportDetails.furniture_car_transport")}</h4>
          <p className="sub-title">
            Transport ID: {transport?.booking_id} | {translateStatus(transport?.status)}
          </p>

          {loading && (
            <div className="text-center py-5">
              <Spinner animation="border" className="text-orange" />
              <p className="mt-2 text-white">{t("clientTransportDetails.loading_data")}</p>
            </div>
          )}

          {error && <Alert variant="danger" className="text-center">{error}</Alert>}

          {!loading && !error && transport && (
            <Row className="g-4 p-3 rounded-3">
              {/* Left - Transport details */}
              <Col md={6}>
                <Card className="detail-card h-100">
                  <Card.Header>
                    <div className="card-header-title">
                      <FaTruck className="icon" />
                      {t("clientTransportDetails.transport_details")}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="transport-number">
                      {t("clientTransportDetails.order_number")} {transport.booking_id}
                      <span className="transport-type-badge">{t(transport.transport_type)}</span>
                    </div>

                    <div className="mb-3">
                      <span className="me-2">Status:</span>
                      <span className={`status-badge ${transport.status}`}>{translateStatus(transport.status)}</span>
                    </div>

                    <div className="section-header">{t("clientTransportDetails.route_information")}</div>
                    <div className="info-row">
                      <FaMapMarkerAlt className="icon text-orange" />
                      <span>{transport.booking_from} ({transport.from_codes})</span>
                    </div>
                    <div className="info-row">
                      <FaMapMarkerAlt className="icon" style={{ color: "#6c6c70" }} />
                      <span>{transport.booking_to} ({transport.to_codes})</span>
                    </div>
                    <div className="info-row mt-2">
                      <FaTruck className="icon" />
                      <span>
                        <strong>{t("clientTransportDetails.distance")}:</strong> {transport.distance}
                        <span className="mx-2">|</span>
                        <FaClock className="icon" style={{ display: "inline", marginRight: 4 }} />
                        {transport.total_duration}
                      </span>
                    </div>

                    <div className="accordion-toggle mt-3" onClick={(e) => {
                      e.currentTarget.classList.toggle("open");
                      e.currentTarget.nextElementSibling?.classList.toggle("d-none");
                    }}>
                      <span>{t("clientTransportDetails.additional_info")}</span>
                      <span className="icon">+</span>
                    </div>
                    <div className="d-none mt-2">
                      {transport.delivery_as && (
                        <div className="info-row">
                          <strong>{t("clientTransportDetails.delivery_as")}:</strong>
                          <span className="ms-1">{t(transport.delivery_as)}</span>
                        </div>
                      )}
                      {(transport.description || transport.user_note) && (
                        <div className="info-row mt-2">
                          <strong>{t("delivery_summary.additional_notes")}:</strong>
                          <span className="ms-1">{transport.description || transport.user_note}</span>
                        </div>
                      )}
                    </div>

                    {transport.images && <ImageGallery images={transport.images} />}

                    <CustomerRatingDisplay
                      rating={transport.customerRating}
                      comment={transport.customerRating?.comment || transport.comment}
                    />
                  </Card.Body>
                </Card>
              </Col>

              {/* Right - Loading / Unloading */}
              <Col md={6}>
                <Card className="detail-card h-100">
                  <Card.Header>
                    <div className="card-header-title">
                      <FaBox className="icon" />
                      {t("clientTransportDetails.loading_unloading")}
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <div className="info-section">
                      <div className="info-section-title">{t("clientTransportDetails.loading_info")}</div>
                      {(transport.loading_date_start || transport.loading_date) && (
                        <div className="info-item">
                          <FaCalendarAlt className="icon" />
                          <span className="label">{t("clientTransportDetails.date")}:</span>
                          <span className="value">{transport.loading_date || transport.loading_date_start}</span>
                        </div>
                      )}
                      {transport.loading_time && (
                        <div className="info-item">
                          <FaClock className="icon" />
                          <span className="label">{t("clientTransportDetails.time")}:</span>
                          <span className="value">{transport.loading_time}</span>
                        </div>
                      )}
                      {transport.loading_name && (
                        <div className="info-item"><span className="label">{t("Name")}:</span><span className="value">{transport.loading_name}</span></div>
                      )}
                      {transport.loading_streat && (
                        <div className="info-item"><span className="label">{t("clientTransportDetails.address")}:</span><span className="value">{transport.loading_streat}</span></div>
                      )}
                      {transport.loading_phone && (
                        <div className="info-item"><span className="label">{t("clientTransportDetails.phone_number")}:</span><span className="value">{transport.loading_phone}</span></div>
                      )}
                    </div>

                    <div className="info-section">
                      <div className="info-section-title">{t("clientTransportDetails.unloading_info")}</div>
                      {(transport.unloading_date_start || transport.unloading_date) && (
                        <div className="info-item">
                          <FaCalendarAlt className="icon" />
                          <span className="label">{t("clientTransportDetails.date")}:</span>
                          <span className="value">{transport.unloading_date || transport.unloading_date_start}</span>
                        </div>
                      )}
                      {transport.unloading_time && (
                        <div className="info-item">
                          <FaClock className="icon" />
                          <span className="label">{t("clientTransportDetails.time")}:</span>
                          <span className="value">{transport.unloading_time}</span>
                        </div>
                      )}
                      {transport.unloading_name && (
                        <div className="info-item"><span className="label">{t("Name")}:</span><span className="value">{transport.unloading_name}</span></div>
                      )}
                      {transport.unloading_streat && (
                        <div className="info-item"><span className="label">{t("clientTransportDetails.address")}:</span><span className="value">{transport.unloading_streat}</span></div>
                      )}
                      {transport.unloading_phone && (
                        <div className="info-item"><span className="label">{t("clientTransportDetails.phone_number")}:</span><span className="value">{transport.unloading_phone}</span></div>
                      )}
                    </div>

                    {/* Items */}
                    <div className="info-section">
                      <div className="info-section-title">{t("clientTransportDetails.items_information")}</div>

                      {transport.item_breakable != null && (
                        <div className="info-item mb-2">
                          <FaQuestionCircle className="icon" />
                          <span className="label">{t("dynamic_form.fragile_question")}:</span>
                          <span className="value">{transport.item_breakable == "1" ? t("dynamic_form.yes") : t("dynamic_form.no")}</span>
                          {transport.item_breakable_details && <span className="value ms-1">, {transport.item_breakable_details}</span>}
                        </div>
                      )}
                      {transport.special_treatment != null && (
                        <div className="info-item mb-2">
                          <FaQuestionCircle className="icon" />
                          <span className="label">{t("dynamic_form.special_handling_question")}:</span>
                          <span className="value">{transport.special_treatment == "1" ? t("dynamic_form.yes") : t("dynamic_form.no")}</span>
                          {transport.special_treatment_details && <span className="value ms-1">, {transport.special_treatment_details}</span>}
                        </div>
                      )}

                      <div className="mt-3">
                        <div className="mb-2 fw-semibold">{t("clientTransportDetails.items")}:</div>
                        {transport.item_type?.map((item, index) => (
                          <div key={index} className="item-card mb-2">
                            <div className="d-flex justify-content-between align-items-start">
                              <div className="item-details">
                                <div className="item-name fw-semibold">
                                  {item} × {transport.num_items?.[index] || 1}
                                </div>
                                {transport.dimensions?.[index] && (
                                  <div className="text-muted small">
                                    <FaRulerCombined className="me-1" />
                                    {transport.dimensions[index]} cm
                                  </div>
                                )}
                                {(transport.item_weight?.[index] || transport.weight?.[index]) && (
                                  <div className="text-muted small">
                                    <FaWeightHanging className="me-1" />
                                    {formatNumber(transport.item_weight?.[index] || transport.weight?.[index])} kg
                                  </div>
                                )}
                                {transport.board_number?.[index] && (
                                  <div className="text-muted small">
                                    <AiFillIdcard className="me-1" />
                                    {transport.board_number[index]}
                                  </div>
                                )}
                                {transport.making_year?.[index] && (
                                  <div className="text-muted small">
                                    <FaCalendar className="me-1" />
                                    {transport.making_year[index]}
                                  </div>
                                )}
                                {transport.driving?.[index] && (
                                  <div className="text-muted small">
                                    <HiWrenchScrewdriver className="me-1" />
                                    {t("delivery_summary.driving")}: {transport.driving[index] === "1" ? t("dynamic_form.driving_yes") : t("dynamic_form.driving_no")}
                                  </div>
                                )}
                              </div>
                              <Badge className="item-badge">#{index + 1}</Badge>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="info-item mt-3 mb-2 pt-3 border-top">
                        <FaWeight className="icon" />
                        <span className="label">{t("clientTransportDetails.total_weight")}:</span>
                        <span className="value fw-bold">{formatNumber(transport.total_weight)} kg</span>
                      </div>

                      {transport?.price_offer && (
                        <div className="info-item mt-3 mb-0 pt-3 border-top">
                          <FaMoneyBill className="icon" />
                          <span className="label">{t("delivery_summary.priceOffer.title")}:</span>
                          <span className="value fw-bold">{transport.price_offer} €</span>
                        </div>
                      )}

                      <div className="info-item mt-3 mb-2 pt-3 border-top">
                        <FaQuestionCircle className="icon" />
                        <span className="value fw-bold">{t("delivery_summary.paymentTerm.title", "Zahlungsziel")}</span>
                        <br />
                        <div>
                          <span className="label">{t("delivery_summary.paymentTerm.hint", "Zahlungsziel")}</span>
                          <span className="value fw-bold">
                            {PAYMENT_TERM_OPTIONS.find((o) => o.value === transport.payment_term)?.label || "—"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* Orders list - view only */}
          {!loading && orders.length > 0 && (
            <Card className="detail-card mt-4">
              <Card.Header>
                <div className="card-header-title">
                  <FaBox className="icon" /> {t("clientTransportDetails.orders")} ({orders.length})
                </div>
              </Card.Header>
              <Card.Body>
                {orders.map((order) => {
                  const rating = order?.driverRating || order?.driver_rating || null;
                  return (
                    <Card key={order.id} className="p-3 shadow-sm border-0 mb-4 bg-grey-light" style={{ borderRadius: 16 }}>
                      <Row className="align-items-center">
                        <Col md={8}>
                          <h6 className="fw-bold text-orange">{order.driver_name || "No Driver Assigned"}</h6>
                          <div className="mb-2">
                            <small className="text-muted">{t("clientTransportDetails.vehicle")}: </small>
                            <span>{order.vehicle_type || "N/A"} - {order.vehicle_number || "N/A"}</span>
                          </div>
                          <div className="d-flex align-items-center mb-2">
                            <FaStar className="text-orange me-1" />
                            <span className="me-2">{order.average_rating || "No ratings"}</span>
                            <small className="text-muted">({order.total_reviews || 0} reviews)</small>
                          </div>
                        </Col>
                        <Col md={4} className="d-flex flex-column justify-content-center align-items-center gap-2">
                          <Badge bg={getStatusVariant(order.status)} className="mb-2 text-uppercase fw-bold">
                            {translateStatus(order.status)}
                          </Badge>
                          <div className="text-center mb-3">
                            <h5 className="fw-bold text-success mb-1">
                              <FaEuroSign className="me-1" /> {formatNumber(order.price)} {t("netto")}
                            </h5>
                            <small className="text-muted">{t("clientTransportDetails.order")}: {order.order_id}</small>
                          </div>
                          <Button onClick={() => handleShowOrder(order)} className="bg-orange text-white fw-bold border-0">
                            {t("clientTransportDetails.view_order_details")}
                          </Button>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Card.Body>
            </Card>
          )}

          {!loading && !error && orders.length === 0 && (
            <div className="no-orders-message">{t("clientTransportDetails.no_orders")}</div>
          )}

          <EmployeeOrderDetailsModal
            employeeId={employeeId}
            show={showOrder}
            handleClose={() => setShowOrder(false)}
            order={selectedOrder}
            unreadCount={
              selectedOrder ? ordersUnreadCounts[selectedOrder.id] || 0 : 0
            }
            onMessagesRead={(orderId) => {
              // تحديث حالة الرسائل غير المقروءة عند قراءة الرسائل
              updateOrderUnreadCount(orderId, 0);
            }}
          />
        </Container>
      </div>

      <Footer />

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}